// QM Handbuch API helpers — wrap the same endpoints the legacy app talks to
// under /api/qm/handbuch/<slug>/* and /api/qm/documents/<slug>.

import type { QmPerson, QmReferenz } from "./state";

export interface QmInput {
  meta: { created_at: string; company_slug: string; website_url: string };
  stammdaten: {
    unternehmensname: string;
    rechtsform: string;
    sitz_adresse: string;
    gruendung: string;
    geschaeftsfuehrung: string;
    mitarbeitende: string;
    kurzprofil: string;
  };
  beratungsfelder: {
    kern_beratungsfelder: string[];
    ergaenzende_umsetzungsleistungen: string[];
    nicht_angeboten: string;
  };
  zielgruppen: {
    hauptzielgruppen: string[];
    branchenfokus: string;
    typische_projektanlaesse: string[];
  };
  team_qualifikation: {
    schluesselpersonen: QmPerson[];
    referenzen_projektbeispiele: QmReferenz[];
  };
  personal_infrastruktur: {
    personalgewinnung: string;
    weiterbildung: string;
    kapazitaeten: string;
    infrastruktur_tools: string;
    netzwerke_partner: string;
  };
  beratungsprozess: {
    erstkontakt: string;
    bedarfsermittlung: string;
    auftragsklarung: string;
    durchfuehrung: string;
    dokumentation: string;
    feedback_nachbetreuung: string;
  };
  qualitaetsmanagement: {
    qualitaetsziele: string[];
    interne_kontrollen: string;
    beschwerden_abweichungen: string;
    kvp_lessons_learned: string;
    risiken_chancen: string;
    interne_ueberpruefung: string;
  };
}

export async function saveQmInput(slug: string, payload: QmInput): Promise<void> {
  const r = await fetch("/api/qm/handbuch/" + encodeURIComponent(slug), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: r.statusText }));
    throw new Error(err.error || r.statusText || "Speichern fehlgeschlagen");
  }
}

export async function saveQmDocuments(slug: string, content: string): Promise<void> {
  // Non-fatal on failure: agent will still run without the docs file.
  await fetch("/api/qm/documents/" + encodeURIComponent(slug), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
    credentials: "include",
  }).catch(() => null);
}

export async function uploadQmLogo(slug: string, file: File): Promise<{ ok: boolean; error?: string }> {
  const fd = new FormData();
  fd.append("logo", file);
  const r = await fetch("/api/qm/handbuch/" + encodeURIComponent(slug) + "/logo", {
    method: "PUT",
    body: fd,
    credentials: "include",
  }).catch(() => null);
  if (!r) return { ok: false, error: "Netzwerkfehler" };
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: r.statusText }));
    return { ok: false, error: err.error ?? r.statusText };
  }
  return { ok: true };
}

export async function fetchQmHandbuch(slug: string): Promise<string | null> {
  const r = await fetch("/api/qm/handbuch/" + encodeURIComponent(slug) + "/md", {
    credentials: "include",
  }).catch(() => null);
  if (!r || !r.ok) return null;
  const data = (await r.json()) as { content?: string };
  return data.content ?? "";
}

export async function saveQmHandbuch(slug: string, content: string): Promise<void> {
  const r = await fetch("/api/qm/handbuch/" + encodeURIComponent(slug) + "/md", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
    credentials: "include",
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: r.statusText }));
    throw new Error(err.error || r.statusText || "Speichern fehlgeschlagen");
  }
}

export async function downloadQmFile(
  slug: string,
  endpoint: "pdf" | "docx",
): Promise<{ ok: boolean; error?: string }> {
  const r = await fetch(`/api/qm/handbuch/${encodeURIComponent(slug)}/${endpoint}`, {
    credentials: "include",
  }).catch(() => null);
  if (!r) return { ok: false, error: "Netzwerkfehler" };
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: r.statusText }));
    return { ok: false, error: err.error ?? r.statusText };
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `qm_handbuch_${slug}.${endpoint}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { ok: true };
}
